import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Target, Users, Phone, Car, TrendingUp, Calendar, ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import { API } from '@/utils/api';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const MONTHS = ['Janvier', 'Fevrier', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Aout', 'Septembre', 'Octobre', 'Novembre', 'Decembre'];

const ProgressBar = ({ actual, target, color = 'bg-blue-500' }) => {
  const pct = target > 0 ? Math.min((actual / target) * 100, 100) : 0;
  return (
    <div className="w-full bg-slate-100 rounded-full h-2.5">
      <div className={`h-2.5 rounded-full transition-all ${color}`} style={{ width: `${pct}%` }} />
    </div>
  );
};

const StatCard = ({ icon: Icon, label, actual, target, color, bgColor }) => {
  const pct = target > 0 ? Math.round((actual / target) * 100) : 0;
  return (
    <Card className={`p-4 border-l-4 ${color}`} data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${bgColor}`}>
            <Icon className="w-4 h-4 text-white" />
          </div>
          <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">{label}</span>
        </div>
        {target > 0 && (
          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
            pct >= 100 ? 'bg-emerald-100 text-emerald-700' : pct >= 50 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
          }`}>{pct}%</span>
        )}
      </div>
      <div className="flex items-end gap-1 mb-2">
        <span className="text-2xl font-bold text-slate-900">{actual}</span>
        {target > 0 && <span className="text-sm text-slate-400 mb-0.5">/ {target}</span>}
      </div>
      {target > 0 && <ProgressBar actual={actual} target={target} color={bgColor} />}
    </Card>
  );
};

const SalesDashboard = ({ auth }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const employeeId = auth?.admin?.id;

  const loadStats = useCallback(async () => {
    if (!employeeId) return;
    try {
      const res = await axios.get(`${API}/dashboard/my-stats/${employeeId}?month=${month}&year=${year}`);
      setStats(res.data);
    } catch (e) { console.error(e); }
  }, [employeeId, month, year]);

  useEffect(() => { loadStats(); }, [loadStats]);

  const prevMonth = () => {
    if (month === 1) { setMonth(12); setYear(y => y - 1); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (month === 12) { setMonth(1); setYear(y => y + 1); }
    else setMonth(m => m + 1);
  };

  if (!stats) return <div className="text-center py-12 text-slate-400">Chargement...</div>;

  const o = stats.objectives;

  return (
    <div className="space-y-6" data-testid="sales-dashboard">
      {/* Month selector */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-slate-900">Mon tableau de bord</h2>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={prevMonth} data-testid="prev-month"><ChevronLeft className="w-4 h-4" /></Button>
          <span className="text-sm font-medium text-slate-600 min-w-[120px] text-center">{MONTHS[month - 1]} {year}</span>
          <Button variant="ghost" size="sm" onClick={nextMonth} data-testid="next-month"><ChevronRight className="w-4 h-4" /></Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="kpi-cards">
        <StatCard icon={Car} label="Ventes vehicules" actual={o.vehicle_sales_actual} target={o.vehicle_sales_target}
          color="border-l-emerald-500" bgColor="bg-emerald-500" />
        <StatCard icon={Users} label="Nouveaux contacts" actual={o.new_contacts_actual} target={o.new_contacts_target}
          color="border-l-blue-500" bgColor="bg-blue-500" />
        <StatCard icon={Phone} label="Relances" actual={o.follow_ups_actual} target={o.follow_ups_target}
          color="border-l-violet-500" bgColor="bg-violet-500" />
      </div>

      {/* Commission summary */}
      {stats.sales.length > 0 && (
        <Card className="p-4 border-l-4 border-l-amber-500" data-testid="commission-card">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="w-4 h-4 text-amber-600" />
            <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Commissions du mois</span>
          </div>
          <div className="text-2xl font-bold text-slate-900 mb-3">CHF {stats.total_commission.toLocaleString('fr-CH')}</div>
          <div className="space-y-2">
            {stats.sales.map(s => (
              <div key={s.id} className="flex items-center justify-between text-sm bg-slate-50 rounded px-3 py-2">
                <div>
                  <span className="font-medium">{s.vehicle_info?.brand} {s.vehicle_info?.model}</span>
                  <span className="text-slate-400 ml-2">- {s.client_name}</span>
                </div>
                <span className="font-bold text-amber-700">CHF {s.commission_amount?.toLocaleString('fr-CH')}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Today's follow-ups */}
      <Card className="p-4" data-testid="todays-followups">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-red-500" />
            <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">
              Relances du jour ({stats.todays_follow_ups.length})
            </span>
          </div>
          <Button size="sm" variant="outline" onClick={() => navigate('/admin/clients')} data-testid="view-all-clients">
            <Plus className="w-3.5 h-3.5 mr-1" /> Nouveau contact
          </Button>
        </div>
        {stats.todays_follow_ups.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-4">Aucune relance pour aujourd'hui</p>
        ) : (
          <div className="space-y-2">
            {stats.todays_follow_ups.map(c => (
              <div key={c.id} className="flex items-center justify-between p-3 bg-red-50 border border-red-100 rounded-lg cursor-pointer hover:bg-red-100 transition-colors"
                onClick={() => navigate(`/admin/clients/${c.id}`)} data-testid={`followup-${c.id}`}>
                <div>
                  <p className="text-sm font-medium text-slate-900">{c.first_name} {c.last_name}</p>
                  <p className="text-xs text-slate-500">
                    {c.current_vehicle_brand && `${c.current_vehicle_brand} ${c.current_vehicle_model} - `}
                    {c.phone}
                  </p>
                </div>
                <div className="text-right">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${
                    c.status === 'client' ? 'bg-emerald-100 text-emerald-700' :
                    c.status === 'prospect' ? 'bg-blue-100 text-blue-700' :
                    'bg-slate-100 text-slate-600'
                  }`}>{c.status}</span>
                  <p className="text-[10px] text-red-500 mt-0.5">{c.next_follow_up_date}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Recent clients created */}
      <Card className="p-4" data-testid="recent-clients">
        <div className="flex items-center gap-2 mb-3">
          <Users className="w-4 h-4 text-blue-500" />
          <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">
            Mes contacts recents
          </span>
        </div>
        {stats.recent_clients.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-4">Aucun contact cree</p>
        ) : (
          <div className="divide-y">
            {stats.recent_clients.slice(0, 10).map(c => (
              <div key={c.id} className="flex items-center justify-between py-2 cursor-pointer hover:bg-slate-50 px-2 rounded"
                onClick={() => navigate(`/admin/clients/${c.id}`)}>
                <div>
                  <p className="text-sm font-medium">{c.first_name} {c.last_name}</p>
                  <p className="text-xs text-slate-400">
                    {c.current_vehicle_brand && `${c.current_vehicle_brand} ${c.current_vehicle_model}`}
                    {c.phone && ` - ${c.phone}`}
                  </p>
                </div>
                <div className="text-right">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${
                    c.status === 'client' ? 'bg-emerald-100 text-emerald-700' :
                    c.status === 'prospect' ? 'bg-blue-100 text-blue-700' :
                    'bg-slate-100 text-slate-600'
                  }`}>{c.status}</span>
                  {c.next_follow_up_date && (
                    <p className="text-[10px] text-slate-400 mt-0.5">Relance: {c.next_follow_up_date}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Client distribution */}
      {Object.keys(stats.client_status_distribution).length > 0 && (
        <Card className="p-4" data-testid="client-distribution">
          <div className="flex items-center gap-2 mb-3">
            <Target className="w-4 h-4 text-slate-500" />
            <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Repartition de mes clients</span>
          </div>
          <div className="flex gap-4">
            {Object.entries(stats.client_status_distribution).map(([status, count]) => (
              <div key={status} className="text-center flex-1">
                <div className={`text-2xl font-bold ${
                  status === 'client' ? 'text-emerald-600' : status === 'prospect' ? 'text-blue-600' : 'text-slate-500'
                }`}>{count}</div>
                <div className="text-xs text-slate-500 capitalize">{status === 'ancien_client' ? 'Ancien' : status}s</div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};

export default SalesDashboard;
